import React, { useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useFetchWithTimeWindow } from '../../hooks/useFetchWithTimeWindow';
import { useUsageAgents, useUsageModels, useUsageWorkflows } from '../../services/appDetailApi';
import { ViewTabs } from '../../components';
import CostAnalysisGrid from '../../components/analysis/CostAnalysisGrid';
import CostBreakdownSection from '../../components/analysis/CostBreakdownSection';
import CostInsightsPanel from '../../components/analysis/CostInsightsPanel';
import CostSummaryPanel from '../../components/analysis/CostSummaryPanel';
import { MOCK_COST_DATA } from '../../components/analysis/costMockData';
import { isInDemoMode } from '../../services/demoDataService';
import { components } from '../../utils/designTokens';
import { serializeFilters } from '../../utils/urlBuilder';
import { LOADING_MESSAGE } from '../../utils/strings';
import { totalModelTokens } from '../../utils/tokenMetrics';
import {
  aggregateAgentUsageRows,
  aggregateModelUsageRows,
  normalizeWorkflowUsageRows,
} from '../../utils/costBreakdownRows';

const COST_SUBTABS = [
  { value: 'breakdown', label: 'Spend Breakdown' },
  { value: 'trends', label: 'Cost Trends' },
];

/**
 * CostTab - Cost analysis tab content for AppDetails
 *
 * Self-contained component that fetches cost data and renders charts.
 * No layout wrapper or breadcrumbs - rendered inside AppDetails' TabbedPageLayout.
 */
export default function CostTab() {
  const { selectedAppId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedEnvironment } = useEnvironment();
  const { selectedRange } = useFetchWithTimeWindow();
  const { session } = useStytchMemberSession();

  const tenantId = session?.organization_id || null;
  const inDemoMode = isInDemoMode(tenantId, selectedEnvironment, selectedAppId);

  const v2AppId = !inDemoMode ? selectedAppId : null;
  const { rows: v2AgentRows, loading: v2AgentsLoading, error: v2AgentsError } = useUsageAgents(v2AppId);
  const { rows: v2ModelRows, loading: v2ModelsLoading, error: v2ModelsError } = useUsageModels(v2AppId);
  const { rows: v2WfRows,    loading: v2WfsLoading,    error: v2WfsError    } = useUsageWorkflows(v2AppId);

  // Normalize v2 flat model rows into the bucketed {timestamp, models:[...]} shape
  // that CostAnalysisGrid expects. Falls through to v1 costData when not in v2 mode.
  const modelCostOverTimeData = useMemo(() => {
    if (inDemoMode) return MOCK_COST_DATA.model_cost_over_time;
    if (!v2ModelRows) return [];
    const byBucket = {};
    for (const r of v2ModelRows) {
      if (!byBucket[r.bucket_time]) byBucket[r.bucket_time] = [];
      byBucket[r.bucket_time].push({
        model_name: r.model_name, provider: r.llm_provider,
        cost_usd: r.cost_usd, prompt_tokens: r.input_tokens,
        completion_tokens: r.output_tokens, cached_tokens: r.cached_tokens,
        total_tokens: totalModelTokens(r.input_tokens, r.output_tokens),
      });
    }
    return Object.entries(byBucket)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([ts, models]) => ({ timestamp: ts, models: [...models].sort((a, b) => b.cost_usd - a.cost_usd) }));
  }, [inDemoMode, v2ModelRows]);

  // Normalize v2 flat agent rows into {timestamp, agents:[...]} shape.
  const agentCostOverTimeData = useMemo(() => {
    if (inDemoMode) return MOCK_COST_DATA.agent_cost_over_time;
    if (!v2AgentRows) return [];
    const byBucket = {};
    for (const r of v2AgentRows) {
      if (!byBucket[r.bucket_time]) byBucket[r.bucket_time] = [];
      byBucket[r.bucket_time].push({
        agent_name: r.agent_name, cost_usd: r.cost_usd,
        prompt_tokens: r.input_tokens, completion_tokens: r.output_tokens,
        cached_tokens: r.cached_tokens,
        total_tokens: totalModelTokens(r.input_tokens, r.output_tokens),
      });
    }
    return Object.entries(byBucket)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([ts, agents]) => ({ timestamp: ts, agents: [...agents].sort((a, b) => b.cost_usd - a.cost_usd) }));
  }, [inDemoMode, v2AgentRows]);

  // Derive summary from v2 workflow rows (total cost + trace count).
  // Previous-window deltas are null in v2 (not yet implemented).
  const summary = useMemo(() => {
    if (inDemoMode) return MOCK_COST_DATA.summary;
    if (!v2WfRows) return null;
    const totalCost = v2WfRows.reduce((s, r) => s + (r.cost_usd || 0), 0);
    const traceCount = v2WfRows.reduce((s, r) => s + (r.trace_count || 0), 0);
    return {
      total_cost_usd:         totalCost,
      trace_count:            traceCount,
      avg_cost_per_trace_usd: traceCount > 0 ? totalCost / traceCount : 0,
      previous_total_cost_usd: null,
      delta_cost_usd:          null,
      delta_cost_pct:          null,
    };
  }, [inDemoMode, v2WfRows]);

  // Normalize v2 workflow rows into the CostBreakdownSection row shape.
  const workflowRows = useMemo(() => {
    if (inDemoMode) return MOCK_COST_DATA.workflow_cost_breakdown;
    if (!v2WfRows) return [];
    return normalizeWorkflowUsageRows(v2WfRows);
  }, [inDemoMode, v2WfRows]);

  const agentRows = useMemo(() => {
    if (inDemoMode) return MOCK_COST_DATA.agent_cost_breakdown;
    if (!v2AgentRows) return [];
    return aggregateAgentUsageRows(v2AgentRows);
  }, [inDemoMode, v2AgentRows]);

  const modelRows = useMemo(() => {
    if (inDemoMode) return MOCK_COST_DATA.model_cost_breakdown;
    if (!v2ModelRows) return [];
    return aggregateModelUsageRows(v2ModelRows);
  }, [inDemoMode, v2ModelRows]);

  const effectiveLoading = !inDemoMode && (v2AgentsLoading || v2ModelsLoading || v2WfsLoading);

  const effectiveError = !inDemoMode && (v2AgentsError || v2ModelsError || v2WfsError);

  const activeSubTab = searchParams.get('cost_subtab') || 'breakdown';

  const handleSubTabChange = (nextTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('cost_subtab', nextTab);
    setSearchParams(nextParams);
  };

  const handleRowClick = (dimensionKey, row) => {
    const value = row?.filterValue || row?.label || row?.key;
    if (!value) return;
    const filters = { [dimensionKey]: value, env: selectedEnvironment, range: selectedRange, from: 'cost' };
    navigate(`/app/${selectedAppId}/traces?${serializeFilters(filters)}`);
  };

  if (effectiveLoading) {
    return <div className={components.table.empty}>{LOADING_MESSAGE}</div>;
  }

  if (effectiveError && !inDemoMode) {
    return <div className={components.table.empty}><div className="text-danger">{String(effectiveError)}</div></div>;
  }

  return (
    <div className="flex flex-col gap-6 pb-8">
      <ViewTabs
        tabs={COST_SUBTABS}
        activeTab={activeSubTab}
        onTabChange={handleSubTabChange}
      />
      {activeSubTab === 'breakdown' ? (
        <>
          {summary ? (
            <CostSummaryPanel
              summary={summary}
              workflowRows={workflowRows}
              timeWindow={selectedRange}
              onDimensionClick={(dim, value) => handleRowClick(dim, { label: value })}
            />
          ) : null}
          <CostBreakdownSection
            title="Business Task Cost"
            dimensionKey="workflow"
            rows={workflowRows}
            loading={effectiveLoading}
            error={effectiveError}
            appId={selectedAppId}
            environment={selectedEnvironment}
            timeWindow={selectedRange}
            collapsedRowCount={5}
            onRowClick={(row) => handleRowClick('workflow', row)}
          />
          <CostBreakdownSection
            title="Agent Cost"
            dimensionKey="agent"
            rows={agentRows}
            loading={effectiveLoading}
            error={effectiveError}
            appId={selectedAppId}
            environment={selectedEnvironment}
            timeWindow={selectedRange}
            collapsedRowCount={5}
            onRowClick={(row) => handleRowClick('agent', row)}
          />
          <CostBreakdownSection
            title="Model Cost"
            dimensionKey="model"
            rows={modelRows}
            loading={effectiveLoading}
            error={effectiveError}
            appId={selectedAppId}
            environment={selectedEnvironment}
            timeWindow={selectedRange}
            collapsedRowCount={5}
            onRowClick={(row) => handleRowClick('model', row)}
          />
        </>
      ) : (
        <>
          {inDemoMode && (
            <CostInsightsPanel
              appId={selectedAppId}
              environment={selectedEnvironment}
              range={selectedRange}
            />
          )}
          <CostAnalysisGrid
            modelCostOverTimeData={modelCostOverTimeData}
            agentCostOverTimeData={agentCostOverTimeData}
            timeWindow={selectedRange}
            appId={selectedAppId}
            environment={selectedEnvironment}
          />
        </>
      )}
    </div>
  );
}
